/**
 * Typisierter API-Client + Anti-Corruption-Mapping-Schicht (KONZEPT §9/§10).
 *
 * `apiFetch` ruft das Backend RELATIV unter `/api/...` auf (kein absolutes
 * BASE) — in Dev via Vite-Proxy same-origin, in Prod via @fastify/static
 * same-host. Cookie-Auth: `credentials: 'include'`; mutierende Requests senden
 * den CSRF-Token aus dem `csrf`-Cookie als Header `X-CSRF-Token`.
 *
 * Die Funktionen holen die kanonischen `*Dto` aus @ads/contracts und mappen
 * sie auf die bestehenden View-Typen (`AccountFixture`, `ChangeSetFixture`, …).
 * Fixtures sind NUR noch hinter `VITE_USE_FIXTURES === '1'` aktiv (Dev-Schalter);
 * im Normalbetrieb gibt es keinen stillen Fallback mehr.
 */
import { log, setLastRequestId } from './log.js';
import type {
  AccountDto,
  AssetDto,
  UploadAssetInput,
  AdsReadDto,
  ProposalDto,
  ChangeItemDto,
  CustomerProfileDto,
  MeetingDto,
  DecisionDto,
  AuditEventDto,
  MeDto,
  SettingsDto,
  RefreshConfigInput,
  InviteInput,
  UpdateRoleInput,
  OwnerRole,
  // Kampagnen-View §10: Tabellen- und Annotations-DTOs
  TableWindowDto,
  CampaignRowDto,
  AdGroupRowDto,
  KeywordRowDto,
  AdRowDto,
  AnnotationDto,
  CreateAnnotationInput,
  UpdateAnnotationInput,
  EntityAnnotationType,
  // Marketing-Stratege (Paket E)
  StrategyDto,
  UpdateProfileContextInput,
  CreateBusinessGoalInput,
  BusinessGoalDto,
  UpdateBusinessGoalInput,
  SetSpendCeilingInput,
  SpendCeilingDto,
  SpendCeilingOverrideInput,
  KeywordIdeasInput,
  KeywordIdeasResultDto,
  CampaignPlanDto,
  CreateCampaignPlanInput,
  CampaignBrief,
  CampaignPlanDraft,
  SubmitCampaignPlanInput,
} from '@ads/contracts';
import type {
  AccountFixture,
  AdBaseline,
  AssetFixture,
  ChangeSetFixture,
  ChangeItemFixture,
  ProfileFixture,
  MeetingFixture,
  DecisionFixture,
  AuditLogEntry,
  SettingsFixture,
  RefreshConfig,
} from './types';

// ---- Reauth-Handler-Registry -----------------------------------------------

/**
 * Wird von useAuth per setReauthHandler registriert. Wenn apiFetch eine
 * 401-Antwort mit code === 'GOOGLE_REAUTH_REQUIRED' erhält, ruft apiFetch
 * diesen Handler auf, bevor es die ApiError wirft. Damit meldet die App den
 * Operator aus, der dann Google-Ads neu autorisieren kann.
 */
let _reauthHandler: (() => void) | null = null;

/**
 * Registriert (oder entfernt mit null) den Reauth-Handler.
 * Wird von useAuth per useEffect gesetzt und beim Unmount bereinigt.
 */
export function setReauthHandler(fn: (() => void) | null): void {
  _reauthHandler = fn;
}

// ---- Fixture-Schalter (nur Dev) -------------------------------------------

/**
 * true = explizit per `VITE_USE_FIXTURES=1` aktivierter Demo-Modus. Dann werden
 * lokale Fixtures statt des Backends genutzt. Ohne diese Variable gibt es KEINEN
 * stillen Fallback — Fehler propagieren an den State-Loader (loading/error).
 */
export const isFixtureMode: boolean =
  (import.meta.env['VITE_USE_FIXTURES'] as string | undefined) === '1';

// ---- CSRF ------------------------------------------------------------------

/** Liest den (nicht-httpOnly) `csrf`-Cookie für das Double-Submit-Token. */
function readCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

// ---- apiFetch --------------------------------------------------------------

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };
  if (WRITE_METHODS.has(method)) {
    const csrf = readCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    ...init,
    headers,
  });

  // x-request-id aus JEDER Antwort lesen und im Logger-Kontext festhalten.
  const requestId = res.headers.get('x-request-id') ?? undefined;
  setLastRequestId(requestId);

  if (!res.ok) {
    // Body parsen: JSON bevorzugt (um error/detail/requestId/code zu extrahieren), sonst raw text.
    let errorMessage = res.statusText;
    let detail: string | undefined;
    let bodyRequestId: string | undefined = requestId;
    let bodyCode: string | undefined;

    const rawBody = await res.text().catch(() => '');
    if (rawBody) {
      try {
        const parsed = JSON.parse(rawBody) as Record<string, unknown>;
        if (typeof parsed['error'] === 'string') errorMessage = parsed['error'];
        if (typeof parsed['detail'] === 'string') detail = parsed['detail'];
        if (typeof parsed['requestId'] === 'string') bodyRequestId = parsed['requestId'];
        if (typeof parsed['code'] === 'string') bodyCode = parsed['code'];
      } catch {
        // Kein JSON → raw text als Meldung nutzen.
        errorMessage = rawBody;
      }
    }

    // Nicht für /api/client-log selbst loggen (verhindert Rekursion).
    if (path !== '/client-log') {
      log.error(`API-Fehler ${res.status} ${path}`, {
        status: res.status,
        path,
        detail,
        requestId: bodyRequestId,
      }, bodyRequestId);
    }

    // Abgelaufener Google-Refresh-Token → Operator ausloggen, damit er sich
    // neu einloggt und Google Ads neu autorisiert.
    // Rekursionsschutz: /auth/me und /auth/logout werden nie umgeleitet,
    // da sie selbst Teil des Login/Logout-Flows sind.
    const isAuthPath = path === '/auth/me' || path === '/auth/logout';
    if (res.status === 401 && !isAuthPath && bodyCode === 'GOOGLE_REAUTH_REQUIRED' && _reauthHandler) {
      _reauthHandler();
    }

    throw new ApiError(res.status, errorMessage, path, bodyRequestId, detail);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Fehler mit HTTP-Status — erlaubt 401/403-Sonderbehandlung im Aufrufer. */
export class ApiError extends Error {
  /** HTTP-Statuscode (z.B. 500, 403). */
  public readonly status: number;
  /** Angefragter API-Pfad (ohne /api-Prefix). */
  public readonly path: string;
  /** Correlation-ID aus x-request-id / requestId-Body-Feld (§1). */
  public readonly requestId?: string;
  /** Echte Fehlerursache aus dem Backend (nur in dev / LOG_VERBOSE, §3). */
  public readonly detail?: string;

  constructor(
    status: number,
    message: string,
    path: string,
    requestId?: string,
    detail?: string,
  ) {
    const fullMsg = detail
      ? `API ${status} (${path}): ${message} — ${detail}`
      : `API ${status} (${path}): ${message}`;
    super(fullMsg);
    this.name = 'ApiError';
    this.status = status;
    this.path = path;
    this.requestId = requestId;
    this.detail = detail;
  }
}

/**
 * Fixture-Gate: liefert im (Dev-)Fixture-Modus den lokalen Wert, sonst das
 * Resultat des echten Calls. Im Normalbetrieb KEIN Fehler-Fallback — Fehler
 * werden geworfen und vom Loader als `error`-State behandelt.
 */
async function withFixtures<T>(
  apiCall: () => Promise<T>,
  fixture: () => T,
): Promise<T> {
  if (isFixtureMode) return fixture();
  return apiCall();
}

// ---- Formatierungs-Helfer (DTO → View) -------------------------------------

const dtFmt = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const dFmt = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/** ISO-String → "10.06.2026, 06:12" (leer bei fehlendem/ungültigem Wert). */
function fmtDateTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : dtFmt.format(d).replace(',', ',');
}

/** ISO-String → "10.06.2026". */
function fmtDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : dFmt.format(d);
}

/** ISO-String → "06:12". */
function fmtTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Relative Zeitangabe "vor 2 Std." aus einem ISO-Timestamp. */
function fmtRelative(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min.`;
  const std = Math.round(min / 60);
  if (std < 24) return `vor ${std} Std.`;
  const tage = Math.round(std / 24);
  return tage === 1 ? 'vor 1 Tag' : `vor ${tage} Tagen`;
}

// ---- Mapper: DTO → View ----------------------------------------------------

/** AccountDto → AccountFixture (11.1). */
export function mapAccount(dto: AccountDto): AccountFixture {
  return {
    id: dto.id,
    name: dto.name,
    cid: dto.customerId,
    industry: dto.industry ?? '',
    open: dto.openProposals,
    lastSync: fmtDateTime(dto.lastSyncAt),
    lastSyncRel: fmtRelative(dto.lastSyncAt),
    spend: dto.spend7d ?? '—',
    trend: 'flat',
  };
}

/** Extrahiert einen Scope-Wert aus einer Payload (Union ohne strikte Typung). */
function scopeField(
  payload: Record<string, unknown> | undefined,
  key: 'campaignName' | 'adGroupName',
): string {
  const scope = (payload?.['scope'] as Record<string, unknown> | undefined) ?? undefined;
  const direct = payload?.[key];
  return (
    (typeof direct === 'string' ? direct : undefined) ??
    (typeof scope?.[key] === 'string' ? (scope[key] as string) : '') ??
    ''
  );
}

/**
 * adGroup-Gruppenüberschrift für ad_group_create: die KI setzt scope.adGroupName
 * nicht zuverlässig (create_proposal-Beschreibung nennt nur campaignId/campaignName),
 * daher Fallback auf den Namen der neu anzulegenden Gruppe im Payload selbst.
 */
function adGroupHeaderFallback(payload: Record<string, unknown>, scopeValue: string): string {
  if (scopeValue) return scopeValue;
  if (payload['type'] === 'ad_group_create') {
    const adGroup = payload['adGroup'] as Record<string, unknown> | undefined;
    return typeof adGroup?.['name'] === 'string' ? (adGroup['name'] as string) : scopeValue;
  }
  if (payload['type'] === 'asset_group_create') {
    const assetGroup = payload['assetGroup'] as Record<string, unknown> | undefined;
    return typeof assetGroup?.['name'] === 'string' ? (assetGroup['name'] as string) : scopeValue;
  }
  return scopeValue;
}

/** ChangeItemDto → ChangeItemFixture (11.2.1). */
export function mapChangeItem(dto: ChangeItemDto): ChangeItemFixture {
  const payload = dto.payloadEffective as unknown as Record<string, unknown>;
  return {
    id: dto.id,
    type: (payload['type'] as ChangeItemFixture['type']) ?? 'keywords',
    campaign: scopeField(payload, 'campaignName'),
    adGroup: adGroupHeaderFallback(payload, scopeField(payload, 'adGroupName')),
    title: dto.title,
    reason: dto.reason ?? '',
    impact: dto.impact ?? '',
    payload,
    status: dto.status,
    edited: dto.edited,
    error: dto.error,
    stateAtProposal: dto.stateAtProposal,
    beforeState: dto.beforeState,
  };
}

/** ProposalDto → ChangeSetFixture (11.2). */
export function mapProposal(dto: ProposalDto): ChangeSetFixture {
  return {
    id: dto.id,
    accountId: dto.accountId,
    title: dto.title,
    createdAt: fmtDateTime(dto.createdAt),
    createdBy: dto.createdBy,
    summary: dto.summary ?? '',
    items: dto.items.map(mapChangeItem),
  };
}

/** CustomerProfileDto → ProfileFixture (11.3). */
export function mapProfile(dto: CustomerProfileDto): ProfileFixture {
  const intro = (dto.readmeMd ?? '').split(/\n\s*\n/)[0]?.trim() ?? '';
  return {
    website: dto.website ?? '',
    readme: {
      updated: fmtDate(dto.readmeUpdatedAt),
      intro,
      facts: [], // view-only; DTO hat keine strukturierten Facts
    },
    tone: dto.tone ?? '',
    dos: dto.dos,
    nogos: dto.nogos,
    assets: dto.assets.map((a) => ({
      name: a.name,
      kind: a.kind,
      hue: a.hue ?? 210,
      googleAssetId: a.googleAssetId,
    })),
  };
}

/** DecisionDto → DecisionFixture (11.4.1). */
function mapDecision(dto: DecisionDto): DecisionFixture {
  return {
    id: dto.id,
    text: dto.text,
    kind: dto.topic ?? 'sonstiges',
    nogo: dto.inNogos || undefined,
    ai: dto.aiState,
  };
}

/** MeetingDto → MeetingFixture (11.4). */
export function mapMeeting(dto: MeetingDto): MeetingFixture {
  return {
    id: dto.id,
    date: fmtDate(dto.date),
    time: fmtTime(dto.date),
    title: dto.title,
    attendees: dto.attendees.map((a) => ({ name: a.name, role: a.role ?? '' })),
    summary: dto.summary,
    aiProcessed: dto.aiProcessed,
    decisions: dto.decisions.map(mapDecision),
  };
}

/** AuditEventDto → AuditLogEntry (11.5). */
export function mapAuditEvent(dto: AuditEventDto): AuditLogEntry {
  const prefix = dto.actor.split(':')[0];
  const actor: 'op' | 'ai' = prefix === 'ai' ? 'ai' : 'op';
  return {
    id: dto.id,
    ts: fmtDateTime(dto.createdAt),
    actor,
    kind: dto.kind,
    text: dto.text,
    ...(dto.kind === 'item_reverted' ? { undone: true } : {}),
  };
}

// ---- Refresh-Config: View ↔ Backend ----------------------------------------

/** RefreshConfigDto-Form (aus SettingsDto) → View-`RefreshConfig` (11.6). */
function mapRefreshConfig(dto: {
  mode: RefreshConfig['mode'];
  hour?: number;
}): RefreshConfig {
  if (dto.mode === 'daily') {
    return { mode: 'daily', time: `${String(dto.hour ?? 6).padStart(2, '0')}:00` };
  }
  if (dto.mode === 'every') {
    return { mode: 'every', everyHours: 12 };
  }
  return { mode: dto.mode };
}

/** View-`RefreshConfig` → Backend-`RefreshConfigInput`. */
function toRefreshConfigInput(cfg: RefreshConfig): RefreshConfigInput {
  if (cfg.mode === 'daily') {
    const hour = cfg.time ? Number.parseInt(cfg.time.split(':')[0] ?? '6', 10) : 6;
    return { mode: 'daily', hour: Number.isNaN(hour) ? 6 : hour };
  }
  return { mode: cfg.mode };
}

/** SettingsDto → SettingsFixture (11.6). */
export function mapSettings(dto: SettingsDto): SettingsFixture {
  const refresh: SettingsFixture['refresh'] = {};
  for (const [accId, cfg] of Object.entries(dto.refresh)) {
    refresh[accId] = mapRefreshConfig(cfg);
  }
  return {
    refresh,
    api: {
      status: dto.api.status,
      lastCheck: dto.api.lastCheck ?? '—',
      developerToken: dto.api.developerTokenMasked ?? '—',
      loginCustomerId: dto.api.loginCustomerId ?? '—',
      clientId: dto.api.clientId ?? '—',
      clientSecret: '—', // Secret NIE an die UI
    },
    users: dto.users.map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      role: u.role,
      status: u.status === 'invited' ? 'invited' : 'active',
      accounts: u.accounts,
    })),
  };
}

// ============================================================================
//  Auth
// ============================================================================

/** Vollständige `/auth/me`-Antwort: Owner + gemappte Konten. */
export interface MeView {
  user: MeDto['owner'];
  accounts: AccountFixture[];
}

/** Browser-Redirect-Ziel für „Mit Google anmelden". */
export const GOOGLE_LOGIN_URL = '/api/auth/google/login';

/** Lädt die aktuelle Session. Wirft `ApiError(401)` wenn nicht eingeloggt. */
export async function getMe(): Promise<MeView> {
  const dto = await apiFetch<MeDto>('/auth/me');
  return { user: dto.owner, accounts: dto.accounts.map(mapAccount) };
}

/** Session beenden (idempotent). */
export async function logout(): Promise<void> {
  await apiFetch<void>('/auth/logout', { method: 'POST' });
}

// ============================================================================
//  Konten
// ============================================================================

export async function listAccounts(): Promise<AccountFixture[]> {
  return withFixtures(
    async () => (await apiFetch<AccountDto[]>('/accounts')).map(mapAccount),
    () => {
      const { ACCOUNTS } = requireFixtures();
      return ACCOUNTS;
    },
  );
}

// ============================================================================
//  Change Set / Proposals
// ============================================================================

export async function getChangeSet(accountId: string): Promise<ChangeSetFixture | null> {
  return withFixtures(
    async () => {
      const dto = await apiFetch<ProposalDto | null>(`/accounts/${accountId}/proposal`);
      return dto ? mapProposal(dto) : null;
    },
    () => {
      const { CHANGE_SET } = requireFixtures();
      return accountId === 'acc-alpen' ? CHANGE_SET : null;
    },
  );
}

/**
 * Alle Proposals eines Kontos (neueste zuerst) — Basis der Mehr-Proposal-Ansicht.
 * GET /accounts/:id/proposals (Plural).
 */
export async function listProposals(accountId: string): Promise<ChangeSetFixture[]> {
  return withFixtures(
    async () => (await apiFetch<ProposalDto[]>(`/accounts/${accountId}/proposals`)).map(mapProposal),
    () => {
      const { CHANGE_SET } = requireFixtures();
      return accountId === 'acc-alpen' ? [CHANGE_SET] : [];
    },
  );
}

/** Item bewerten (approve/reject/deselect/reset) — POST /proposals/:id/items/:itemId/review. */
export async function reviewItem(
  proposalId: string,
  itemId: string,
  action: 'approve' | 'reject' | 'deselect' | 'reset',
): Promise<ChangeItemDto | void> {
  return withFixtures(
    () =>
      apiFetch<ChangeItemDto>(`/proposals/${proposalId}/items/${itemId}/review`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      }),
    () => undefined,
  );
}

/** Editiertes Item speichern — PATCH /proposals/:id/items/:itemId (Payload-Form). */
export async function editItem(
  proposalId: string,
  itemId: string,
  payload: Record<string, unknown>,
): Promise<ChangeItemDto | void> {
  return withFixtures(
    () =>
      apiFetch<ChangeItemDto>(`/proposals/${proposalId}/items/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify({ payload }),
      }),
    () => undefined,
  );
}

/**
 * Proposal an Google Ads pushen — POST /proposals/:id/push.
 * Antwort: die gepushten Items (ChangeItemDto[]) mit ihrem ECHTEN Ergebnis-
 * Status (pushed / push_failed / unknown) — kein ProposalDto.
 * Fixture-Modus: undefined (Demo ohne Backend).
 */
export async function pushProposal(proposalId: string): Promise<ChangeItemDto[] | void> {
  return withFixtures(
    () => apiFetch<ChangeItemDto[]>(`/proposals/${proposalId}/push`, { method: 'POST' }),
    () => undefined,
  );
}

/** Genau ein Item pushen (Status 'approved') — POST /items/:itemId/push. */
export async function pushItem(itemId: string): Promise<ChangeItemDto | void> {
  return withFixtures(
    () => apiFetch<ChangeItemDto>(`/items/${itemId}/push`, { method: 'POST' }),
    () => undefined,
  );
}

/** Ein Proposal per ID neu laden (z. B. echte Item-Status nach Push-Fehler). */
export async function getProposal(proposalId: string): Promise<ChangeSetFixture | null> {
  return withFixtures(
    async () => mapProposal(await apiFetch<ProposalDto>(`/proposals/${proposalId}`)),
    () => null,
  );
}

/** Gepushtes Item revidieren (Gegen-Änderung) — POST /items/:itemId/revert. */
export async function revertItem(itemId: string): Promise<ChangeItemDto | void> {
  return withFixtures(
    () => apiFetch<ChangeItemDto>(`/items/${itemId}/revert`, { method: 'POST' }),
    () => undefined,
  );
}

// ============================================================================
//  Kundenprofil
// ============================================================================

/**
 * Live-Anzeigen eines Kontos → AdBaseline[] (Headlines/Descriptions als String-
 * Listen). Basis für den exakten RSA-Diff (Vorschlag vs. aktueller Ist-Stand).
 */
export async function getAds(accountId: string, adGroupId?: string): Promise<AdBaseline[]> {
  return withFixtures(
    async () => {
      const q = adGroupId ? `?adGroupId=${encodeURIComponent(adGroupId)}` : '';
      // Server liefert seit den PMax-Asset-Gruppen { ads, assetGroups }.
      const { ads } = await apiFetch<AdsReadDto>(`/accounts/${accountId}/ads${q}`);
      return ads.map((a) => ({
        adId: a.adId,
        adGroupId: a.adGroupId,
        headlines: a.headlines.map((h) => h.text),
        descriptions: a.descriptions.map((d) => d.text),
      }));
    },
    () => [],
  );
}

/** Mediathek des Kontos — Grundlage für „Name · Ratio" in der Asset-Gruppen-Ansicht. */
export async function getAssets(accountId: string): Promise<AssetFixture[]> {
  return withFixtures(
    async () => {
      const assets = await apiFetch<AssetDto[]>(`/accounts/${accountId}/assets`);
      return assets.map((a) => ({
        name: a.name,
        kind: a.kind,
        ratio: a.ratio,
        hue: a.hue ?? 210,
        googleAssetId: a.googleAssetId,
      }));
    },
    () => [],
  );
}

export async function getProfile(accountId: string): Promise<ProfileFixture> {
  return withFixtures(
    async () => mapProfile(await apiFetch<CustomerProfileDto>(`/accounts/${accountId}/profile`)),
    () => {
      const { PROFILE } = requireFixtures();
      return PROFILE;
    },
  );
}

export async function updateProfile(
  accountId: string,
  updates: Partial<ProfileFixture>,
): Promise<ProfileFixture> {
  // View → UpdateProfileInput: nur die vom Backend akzeptierten Felder.
  const body: Record<string, unknown> = {};
  if (updates.website !== undefined) body['website'] = updates.website;
  if (updates.tone !== undefined) body['tone'] = updates.tone;
  if (updates.dos !== undefined) body['dos'] = updates.dos;
  if (updates.nogos !== undefined) body['nogos'] = updates.nogos;
  if (updates.readme?.intro !== undefined) body['readmeMd'] = updates.readme.intro;

  return withFixtures(
    async () =>
      mapProfile(
        await apiFetch<CustomerProfileDto>(`/accounts/${accountId}/profile`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      ),
    () => {
      const { PROFILE } = requireFixtures();
      return { ...PROFILE, ...updates } as ProfileFixture;
    },
  );
}

/** Mediathek-Upload: Bild (Base64) oder YouTube-ID → Google Ads Asset-Bibliothek + Profil. */
export async function uploadAsset(accountId: string, input: UploadAssetInput): Promise<AssetDto> {
  return withFixtures(
    () =>
      apiFetch<AssetDto>(`/accounts/${accountId}/assets`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    () => ({
      id: `fx-${Date.now()}`,
      name: input.name,
      kind: input.kind === 'image' ? 'Bild' : 'Video',
      googleAssetId: `fx-${Date.now()}`,
    }),
  );
}

// ============================================================================
//  Terminprotokolle & Entscheidungen
// ============================================================================

export async function getMeetings(accountId: string): Promise<MeetingFixture[]> {
  return withFixtures(
    async () => (await apiFetch<MeetingDto[]>(`/accounts/${accountId}/meetings`)).map(mapMeeting),
    () => {
      const { MEETINGS } = requireFixtures();
      return MEETINGS;
    },
  );
}

export async function createMeeting(
  accountId: string,
  data: Partial<MeetingFixture>,
): Promise<MeetingFixture> {
  return withFixtures(
    async () => {
      const body = {
        accountId,
        title: data.title ?? '',
        // Backend erwartet einen Datums-String; View liefert getrennt date/time.
        date: data.date ?? new Date().toISOString(),
        attendees: (data.attendees ?? []).map((a) => ({ name: a.name, role: a.role || undefined })),
        summary: data.summary ?? '',
      };
      return mapMeeting(
        await apiFetch<MeetingDto>(`/accounts/${accountId}/meetings`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      );
    },
    () => ({
      id: `mtg-${Date.now()}`,
      date: new Date().toLocaleDateString('de-DE'),
      time: '',
      title: data.title ?? 'Neuer Termin',
      attendees: data.attendees ?? [],
      summary: data.summary ?? '',
      aiProcessed: false,
      decisions: [],
      ...data,
    }),
  );
}

export async function updateMeeting(
  id: string,
  patch: Partial<MeetingFixture>,
): Promise<void> {
  const body: Record<string, unknown> = {};
  if (patch.title !== undefined) body['title'] = patch.title;
  if (patch.summary !== undefined) body['summary'] = patch.summary;
  if (patch.attendees !== undefined)
    body['attendees'] = patch.attendees.map((a) => ({ name: a.name, role: a.role || undefined }));
  // date/time werden in der View getrennt geführt; nur senden, wenn date gesetzt.
  if (patch.date !== undefined) body['date'] = patch.date;

  return withFixtures(
    () =>
      apiFetch<void>(`/meetings/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    () => undefined,
  );
}

export async function addDecision(
  meetingId: string,
  data?: { text?: string; category?: string; topic?: string },
): Promise<DecisionFixture> {
  return withFixtures(
    async () =>
      mapDecision(
        await apiFetch<DecisionDto>(`/meetings/${meetingId}/decisions`, {
          method: 'POST',
          body: JSON.stringify({
            meetingId,
            text: data?.text ?? '',
            category: data?.category ?? 'info',
            ...(data?.topic ? { topic: data.topic } : {}),
          }),
        }),
      ),
    () => ({
      id: `nd-${Date.now()}`,
      text: data?.text ?? '',
      kind: 'sonstiges',
      ai: 'open',
    }),
  );
}

export async function updateDecision(
  decisionId: string,
  patch: Partial<DecisionFixture>,
): Promise<void> {
  const body: Record<string, unknown> = {};
  if (patch.text !== undefined) body['text'] = patch.text;
  // View-`kind` 'sonstiges' ist kein Backend-Topic → nicht senden.
  if (patch.kind !== undefined && patch.kind !== 'sonstiges') body['topic'] = patch.kind;

  return withFixtures(
    () =>
      apiFetch<void>(`/decisions/${decisionId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    () => undefined,
  );
}

export async function removeDecision(decisionId: string): Promise<void> {
  return withFixtures(
    () => apiFetch<void>(`/decisions/${decisionId}`, { method: 'DELETE' }),
    () => undefined,
  );
}

// ============================================================================
//  Audit-Log
// ============================================================================

export async function getAuditLog(accountId: string): Promise<AuditLogEntry[]> {
  return withFixtures(
    async () => (await apiFetch<AuditEventDto[]>(`/accounts/${accountId}/audit`)).map(mapAuditEvent),
    () => {
      const { AUDIT_SEED } = requireFixtures();
      return AUDIT_SEED;
    },
  );
}

// ============================================================================
//  Einstellungen & Team
// ============================================================================

export async function getSettings(): Promise<SettingsFixture> {
  return withFixtures(
    async () => mapSettings(await apiFetch<SettingsDto>('/settings')),
    () => {
      const { SETTINGS } = requireFixtures();
      return SETTINGS;
    },
  );
}

export async function updateRefreshConfig(
  accountId: string,
  cfg: RefreshConfig,
): Promise<void> {
  return withFixtures(
    () =>
      apiFetch<void>(`/settings/refresh/${accountId}`, {
        method: 'PUT',
        body: JSON.stringify(toRefreshConfigInput(cfg)),
      }),
    () => undefined,
  );
}

export async function inviteUser(input: InviteInput): Promise<void> {
  return withFixtures(
    () =>
      apiFetch<void>('/settings/users', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    () => undefined,
  );
}

export async function updateUserRole(userId: string, role: OwnerRole): Promise<void> {
  const body: UpdateRoleInput = { role };
  return withFixtures(
    () =>
      apiFetch<void>(`/settings/users/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    () => undefined,
  );
}

export async function removeUser(userId: string): Promise<void> {
  return withFixtures(
    () => apiFetch<void>(`/settings/users/${userId}`, { method: 'DELETE' }),
    () => undefined,
  );
}

// ============================================================================
//  Data-Refresh
// ============================================================================

export async function refreshAccountData(accountId: string): Promise<void> {
  return withFixtures(
    () => apiFetch<void>(`/accounts/${accountId}/refresh`, { method: 'POST' }),
    () => undefined,
  );
}

// ============================================================================
//  Fixtures (NUR im Dev-Modus dynamisch geladen)
// ============================================================================

/**
 * Lädt die Fixtures lazily — der statische Render-Pfad importiert `lib/fixtures`
 * NIE direkt. Dieser Zugriff ist ausschließlich hinter `isFixtureMode` erreichbar.
 */
let fixturesCache: typeof import('./fixtures') | null = null;
// Im Fixture-Modus synchron vorgeladen (siehe primeFixtures), sonst nie genutzt.

function requireFixtures(): typeof import('./fixtures') {
  if (!fixturesCache) {
    throw new Error(
      'Fixtures nicht geladen. Im VITE_USE_FIXTURES-Modus muss primeFixtures() vor API-Calls awaited werden.',
    );
  }
  return fixturesCache;
}

/**
 * Lädt die Fixture-Daten in den Cache (nur Dev). Muss im Fixture-Modus vor den
 * ersten Loadern awaited werden (state.ts erledigt das beim Mount).
 */
export async function primeFixtures(): Promise<void> {
  if (!isFixtureMode) return;
  if (!fixturesCache) fixturesCache = await import('./fixtures');
}

// ============================================================================
//  Kampagnen-View — Tabellen-Endpoints (Bindings §10)
// ============================================================================

/** Query-Parameter für alle vier Tabellen-Endpoints. */
export interface TableQuery {
  dateFrom: string;
  dateTo: string;
  campaignId?: string;
  adGroupId?: string;
  forceRefresh?: boolean;
}

/** Baut URLSearchParams aus einem TableQuery-Objekt. */
function tableQueryParams(q: TableQuery): URLSearchParams {
  const p = new URLSearchParams();
  p.set('dateFrom', q.dateFrom);
  p.set('dateTo', q.dateTo);
  if (q.campaignId !== undefined) p.set('campaignId', q.campaignId);
  if (q.adGroupId !== undefined) p.set('adGroupId', q.adGroupId);
  if (q.forceRefresh !== undefined) p.set('forceRefresh', String(q.forceRefresh));
  return p;
}

/** Kampagnen-Tabelle für ein Konto im gegebenen Datumsfenster. */
export async function getCampaignTable(
  accountId: string,
  q: TableQuery,
): Promise<TableWindowDto<CampaignRowDto>> {
  const params = tableQueryParams(q);
  return apiFetch<TableWindowDto<CampaignRowDto>>(
    `/accounts/${accountId}/table/campaigns?${params.toString()}`,
  );
}

/** Anzeigengruppen-Tabelle (optional nach campaignId gefiltert). */
export async function getAdGroupTable(
  accountId: string,
  q: TableQuery,
): Promise<TableWindowDto<AdGroupRowDto>> {
  const params = tableQueryParams(q);
  return apiFetch<TableWindowDto<AdGroupRowDto>>(
    `/accounts/${accountId}/table/ad-groups?${params.toString()}`,
  );
}

/** Keywords-Tabelle (optional nach campaignId und adGroupId gefiltert). */
export async function getKeywordTable(
  accountId: string,
  q: TableQuery,
): Promise<TableWindowDto<KeywordRowDto>> {
  const params = tableQueryParams(q);
  return apiFetch<TableWindowDto<KeywordRowDto>>(
    `/accounts/${accountId}/table/keywords?${params.toString()}`,
  );
}

/** Anzeigen-Tabelle (optional nach campaignId und adGroupId gefiltert). */
export async function getAdTable(
  accountId: string,
  q: TableQuery,
): Promise<TableWindowDto<AdRowDto>> {
  const params = tableQueryParams(q);
  return apiFetch<TableWindowDto<AdRowDto>>(
    `/accounts/${accountId}/table/ads?${params.toString()}`,
  );
}

// ============================================================================
//  Kampagnen-View — Annotations-Endpoints (Bindings §10)
// ============================================================================

/** Lädt Anmerkungen eines Kontos, optional nach Typ/Entität/Status gefiltert. */
export async function getAnnotations(
  accountId: string,
  opts?: {
    entityType?: EntityAnnotationType;
    entityId?: string;
    status?: 'open' | 'resolved' | 'all';
  },
): Promise<AnnotationDto[]> {
  const p = new URLSearchParams();
  if (opts?.entityType !== undefined) p.set('entityType', opts.entityType);
  if (opts?.entityId !== undefined) p.set('entityId', opts.entityId);
  if (opts?.status !== undefined) p.set('status', opts.status);
  const qs = p.toString();
  return apiFetch<AnnotationDto[]>(
    `/accounts/${accountId}/annotations${qs ? `?${qs}` : ''}`,
  );
}

/** Legt eine neue Anmerkung für eine Entität an. */
export async function createAnnotation(
  accountId: string,
  input: CreateAnnotationInput,
): Promise<AnnotationDto> {
  return apiFetch<AnnotationDto>(`/accounts/${accountId}/annotations`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Aktualisiert eine bestehende Anmerkung (Text, Status, KI-Notiz). */
export async function updateAnnotation(
  id: string,
  input: UpdateAnnotationInput,
): Promise<AnnotationDto> {
  return apiFetch<AnnotationDto>(`/annotations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Löscht eine Anmerkung unwiderruflich. */
export async function deleteAnnotation(id: string): Promise<void> {
  return apiFetch<void>(`/annotations/${id}`, { method: 'DELETE' });
}

// ============================================================================
//  Marketing-Stratege — Strategie, Ziele, Ausgaben-Limit (Paket E)
// ============================================================================

/** Strategie-Aggregat für ein Konto laden. */
export async function getStrategy(accountId: string): Promise<StrategyDto> {
  return withFixtures(
    () => apiFetch<StrategyDto>(`/accounts/${accountId}/strategy`),
    () => FIXTURE_STRATEGY(accountId),
  );
}

/** Profil-Kontext (Zweck/Positionierung/Zielgruppe) aktualisieren. */
export async function updateProfileContext(
  accountId: string,
  input: UpdateProfileContextInput,
): Promise<StrategyDto> {
  return withFixtures(
    () =>
      apiFetch<StrategyDto>(`/accounts/${accountId}/profile-context`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    () => FIXTURE_STRATEGY(accountId),
  );
}

/** Neues Geschäftsziel anlegen. */
export async function createBusinessGoal(
  accountId: string,
  input: CreateBusinessGoalInput,
): Promise<BusinessGoalDto> {
  return withFixtures(
    () =>
      apiFetch<BusinessGoalDto>(`/accounts/${accountId}/goals`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    () => ({
      id: `goal-${Date.now()}`,
      accountId,
      kind: input.kind,
      targetValue: input.targetValue,
      currency: input.currency,
      timeframeStart: input.timeframeStart,
      timeframeEnd: input.timeframeEnd,
      priority: input.priority ?? 2,
      note: input.note,
      status: 'active',
      createdBy: 'fixture',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }),
  );
}

/** Geschäftsziel bearbeiten. */
export async function updateBusinessGoal(
  accountId: string,
  goalId: string,
  input: UpdateBusinessGoalInput,
): Promise<BusinessGoalDto> {
  return withFixtures(
    () =>
      apiFetch<BusinessGoalDto>(`/accounts/${accountId}/goals/${goalId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    () => ({
      id: goalId,
      accountId,
      kind: input.kind ?? 'other',
      targetValue: input.targetValue ?? 0,
      priority: input.priority ?? 2,
      status: input.status ?? 'active',
      createdBy: 'fixture',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }),
  );
}

/** Geschäftsziel archivieren (DELETE). */
export async function deleteBusinessGoal(accountId: string, goalId: string): Promise<void> {
  return withFixtures(
    () => apiFetch<void>(`/accounts/${accountId}/goals/${goalId}`, { method: 'DELETE' }),
    () => undefined,
  );
}

/** Ausgaben-Limit setzen. */
export async function setSpendCeiling(
  accountId: string,
  input: SetSpendCeilingInput,
): Promise<SpendCeilingDto> {
  return withFixtures(
    () =>
      apiFetch<SpendCeilingDto>(`/accounts/${accountId}/spend-ceiling`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    () => ({
      accountId,
      maxMonthlyMicros: input.maxMonthlyMicros,
      currency: input.currency,
      note: input.note,
      updatedBy: 'fixture',
      updatedAt: new Date().toISOString(),
    }),
  );
}

/** Ceiling übersteuern (nur owner, auditiert). */
export async function overrideSpendCeiling(
  accountId: string,
  input: SpendCeilingOverrideInput,
): Promise<{ ok: true }> {
  return withFixtures(
    () =>
      apiFetch<{ ok: true }>(`/accounts/${accountId}/spend-ceiling/override`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    () => ({ ok: true as const }),
  );
}

// ============================================================================
//  Marketing-Stratege — Keyword-Planer (Paket E)
// ============================================================================

/** Keyword-Ideen vom Planer abrufen. */
export async function fetchKeywordIdeas(
  accountId: string,
  input: KeywordIdeasInput,
): Promise<KeywordIdeasResultDto> {
  return withFixtures(
    () =>
      apiFetch<KeywordIdeasResultDto>(`/accounts/${accountId}/keyword-ideas`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    () => ({
      ideas: [
        { text: 'schuhe kaufen', avgMonthlySearches: 8100, competition: 'HIGH', competitionIndex: 82, lowTopOfPageBidMicros: 400000, highTopOfPageBidMicros: 1200000 },
        { text: 'sportschuhe online', avgMonthlySearches: 3600, competition: 'MEDIUM', competitionIndex: 55, lowTopOfPageBidMicros: 280000, highTopOfPageBidMicros: 800000 },
        { text: 'laufschuhe günstig', avgMonthlySearches: 2900, competition: 'LOW', competitionIndex: 30, lowTopOfPageBidMicros: 180000, highTopOfPageBidMicros: 550000 },
        { text: 'sneaker herren', avgMonthlySearches: 5400, competition: 'HIGH', competitionIndex: 75, lowTopOfPageBidMicros: 350000, highTopOfPageBidMicros: 950000 },
        { text: 'damenschuhe shop', avgMonthlySearches: 1900, competition: 'MEDIUM', competitionIndex: 48, lowTopOfPageBidMicros: 220000, highTopOfPageBidMicros: 660000 },
      ],
      requestId: `fixture-${Date.now()}`,
      fetchedAt: new Date().toISOString(),
    }),
  );
}

// ============================================================================
//  Marketing-Stratege — Kampagnen-Pläne (Paket E)
// ============================================================================

/** Alle Kampagnen-Pläne eines Kontos laden. */
export async function listCampaignPlans(accountId: string): Promise<CampaignPlanDto[]> {
  return withFixtures(
    () => apiFetch<CampaignPlanDto[]>(`/accounts/${accountId}/campaign-plans`),
    () => [],
  );
}

/** Neuen Kampagnen-Plan anlegen. */
export async function createCampaignPlan(input: CreateCampaignPlanInput): Promise<CampaignPlanDto> {
  return withFixtures(
    () =>
      apiFetch<CampaignPlanDto>(`/accounts/${input.accountId}/campaign-plans`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    () => ({
      id: `plan-${Date.now()}`,
      accountId: input.accountId,
      title: input.title,
      status: 'briefing',
      brief: input.brief,
      createdBy: 'fixture',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }),
  );
}

/** Einen Kampagnen-Plan laden. */
export async function getCampaignPlan(planId: string): Promise<CampaignPlanDto> {
  return apiFetch<CampaignPlanDto>(`/campaign-plans/${planId}`);
}

/** Briefing eines Plans aktualisieren (setzt Status auf ai_working). */
export async function updateCampaignBrief(
  planId: string,
  brief: CampaignBrief,
): Promise<CampaignPlanDto> {
  return withFixtures(
    () =>
      apiFetch<CampaignPlanDto>(`/campaign-plans/${planId}/brief`, {
        method: 'PUT',
        body: JSON.stringify({ brief }),
      }),
    () => ({
      id: planId,
      accountId: 'fixture',
      title: 'Fixture Plan',
      status: 'ai_working',
      brief,
      createdBy: 'fixture',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }),
  );
}

/** Draft eines Plans aktualisieren (Operator-Edits). */
export async function updateCampaignDraft(
  planId: string,
  draft: CampaignPlanDraft,
): Promise<CampaignPlanDto> {
  return withFixtures(
    () =>
      apiFetch<CampaignPlanDto>(`/campaign-plans/${planId}/draft`, {
        method: 'PUT',
        body: JSON.stringify({ draft }),
      }),
    () => ({
      id: planId,
      accountId: 'fixture',
      title: 'Fixture Plan',
      status: 'ready',
      draft,
      createdBy: 'fixture',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }),
  );
}

/** Plan einreichen (Veröffentlichung auslösen). */
export async function submitCampaignPlan(
  planId: string,
  input: SubmitCampaignPlanInput,
): Promise<CampaignPlanDto> {
  return withFixtures(
    () =>
      apiFetch<CampaignPlanDto>(`/campaign-plans/${planId}/submit`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    () => ({
      id: planId,
      accountId: 'fixture',
      title: 'Fixture Plan',
      status: 'submitted',
      createdBy: 'fixture',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }),
  );
}

// ---- Fixture-Hilfsdaten (Marketing-Stratege) ---------------------------------

function FIXTURE_STRATEGY(accountId: string): StrategyDto {
  return {
    accountId,
    profileContext: {
      businessPurpose: 'Online-Schuhhandel für Sport- und Freizeitschuhe',
      positioning: 'Große Auswahl, schnelle Lieferung, 30-Tage-Rückgabe',
      targetAudience: '25–45 Jahre, sportaffin, digital-native',
    },
    goals: [
      {
        id: 'goal-fixture-1',
        accountId,
        kind: 'roas',
        targetValue: 4_000_000,
        priority: 1,
        note: 'ROAS mind. 4,0 über alle Kampagnen',
        status: 'active',
        createdBy: 'fixture',
        createdAt: '2026-06-01T00:00:00.000Z',
        updatedAt: '2026-06-01T00:00:00.000Z',
      },
      {
        id: 'goal-fixture-2',
        accountId,
        kind: 'leads',
        targetValue: 200,
        priority: 2,
        note: 'Newsletter-Abos: 200/Monat',
        status: 'active',
        createdBy: 'fixture',
        createdAt: '2026-06-01T00:00:00.000Z',
        updatedAt: '2026-06-01T00:00:00.000Z',
      },
    ],
    ceiling: {
      accountId,
      maxMonthlyMicros: 5_000_000_000,
      currency: 'EUR',
      updatedBy: 'fixture',
      updatedAt: '2026-06-01T00:00:00.000Z',
    },
    usage: {
      ceilingMicros: 5_000_000_000,
      currency: 'EUR',
      existingMonthlyMicros: 3_120_000_000,
      headroomMicros: 1_880_000_000,
      computedAt: new Date().toISOString(),
    },
  };
}
